// The chalk drawing kit from the Gakushiori Chalk design system (components/bundle.js), trimmed to the marks the site uses.
// Every mark is a pure function of time, so a frame can be drawn at any moment; the line re-jitters 8 times a second.

export type Pt = [number, number];
type G = CanvasRenderingContext2D;

export const CHALK = {
  chalk: '#E8E4DA',
  yellow: '#F2B33D',
  blue: '#7FC8F4',
  pink: '#F48FAA',
  red: '#E8503F',
  green: '#9EDC8C',
  lilac: '#C4A8F4',
  ground: '#1D3227',
} as const;
export type ChalkColor = keyof typeof CHALK;

export const HAND = "'Segoe Print','Ink Free','Comic Sans MS',Arial,sans-serif";

const TAU = Math.PI * 2;

function hs(n: number) {
  n = n | 0;
  n = Math.imul(n ^ (n >>> 16), 0x45d9f3b);
  n = Math.imul(n ^ (n >>> 16), 0x45d9f3b);
  n = n ^ (n >>> 16);
  return (n >>> 0) / 4294967296;
}
export const h2 = (a: number, b: number) => hs(a * 374761 + b * 668265 + 1013);
export const clamp = (p: number) => (p < 0 ? 0 : p > 1 ? 1 : p);
export const smooth = (p: number) => {
  p = clamp(p);
  return p * p * (3 - 2 * p);
};
export const seg = (t: number, a: number, b: number) => smooth((t - a) / (b - a));

function sub(pts: Pt[], step: number): Pt[] {
  const out: Pt[] = [];
  for (let k = 0; k < pts.length - 1; k++) {
    const [x0, y0] = pts[k];
    const [x1, y1] = pts[k + 1];
    const n = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0) / step));
    for (let m = 0; m < n; m++) out.push([x0 + ((x1 - x0) * m) / n, y0 + ((y1 - y0) * m) / n]);
  }
  out.push(pts[pts.length - 1]);
  return out;
}
function jit(pts: Pt[], amp: number, seed: number): Pt[] {
  return pts.map(([x, y], k) => {
    const a = amp * (0.65 * Math.sin(k * 0.5 + seed * 1.37) + 0.35 * (h2(seed, k) * 2 - 1));
    const b = amp * (0.65 * Math.cos(k * 0.43 + seed * 2.11) + 0.35 * (h2(seed + 5, k) * 2 - 1));
    return [x + a, y + b];
  });
}
const partial = (p: Pt[], f: number) => (f >= 1 ? p : p.slice(0, Math.floor(p.length * f)));
function path(g: G, p: Pt[]) {
  g.beginPath();
  if (!p.length) return;
  g.moveTo(p[0][0], p[0][1]);
  for (let i = 1; i < p.length; i++) g.lineTo(p[i][0], p[i][1]);
}

/* The chalk line: a firm stroke plus a dusty second pass. f = how much is drawn, boil = the shiver frame. */
export function ln(g: G, pts: Pt[], col: string, f: number, seed: number, boil: number, w = 2.4, alpha = 1) {
  if (f <= 0) return;
  const sp = sub(pts, 7);
  const s = seed + boil;
  g.strokeStyle = col;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  let p = partial(jit(sp, 1.2, s), f);
  if (p.length < 2) return;
  g.globalAlpha = 0.88 * alpha;
  g.lineWidth = w;
  path(g, p);
  g.stroke();
  p = partial(jit(sp, 1.9, s + 50), f);
  g.globalAlpha = 0.3 * alpha;
  g.lineWidth = w * 0.6;
  path(g, p);
  g.stroke();
  g.globalAlpha = 1;
}

/* A hand-drawn ring: overshoots its start and drifts outward, as a circled word does. */
export function ring(cx: number, cy: number, rx: number, ry: number, seed: number): Pt[] {
  const p: Pt[] = [];
  const n = 56;
  const st = -2.4 + h2(seed, 1) * 0.4;
  for (let i = 0; i <= n; i++) {
    const a = st + (TAU * 1.1 * i) / n;
    const d = 1 + (0.07 * i) / n;
    p.push([cx + Math.cos(a) * rx * d, cy + Math.sin(a) * ry * d]);
  }
  return p;
}

export function wave(x0: number, x1: number, y: number, amp = 4, len = 20): Pt[] {
  const p: Pt[] = [];
  const n = Math.max(8, Math.round((x1 - x0) / 6));
  for (let i = 0; i <= n; i++) {
    const x = x0 + ((x1 - x0) * i) / n;
    p.push([x, y + Math.sin(((x - x0) / len) * TAU) * amp]);
  }
  return p;
}

export function curve(x0: number, y0: number, x1: number, y1: number, bend: number): Pt[] {
  const mx = (x0 + x1) / 2;
  const my = (y0 + y1) / 2;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const L = Math.hypot(dx, dy) || 1;
  const cx = mx - (dy / L) * bend;
  const cy = my + (dx / L) * bend;
  const p: Pt[] = [];
  for (let i = 0; i <= 24; i++) {
    const u = i / 24;
    p.push([(1 - u) * (1 - u) * x0 + 2 * (1 - u) * u * cx + u * u * x1, (1 - u) * (1 - u) * y0 + 2 * (1 - u) * u * cy + u * u * y1]);
  }
  return p;
}

let DU: HTMLCanvasElement | null = null;
function dustTex() {
  if (DU) return DU;
  const d = document.createElement('canvas');
  d.width = d.height = 160;
  const x = d.getContext('2d')!;
  const im = x.createImageData(160, 160);
  for (let i = 0; i < 160 * 160; i++) im.data[i * 4 + 3] = h2(i, 4) < 0.3 ? 255 : 0;
  x.putImageData(im, 0, 0);
  DU = d;
  return d;
}
/* Chalk dust: knocks small pits out of everything drawn. Run it last. */
export function dust(g: G, W: number, H: number, a = 0.32) {
  g.save();
  g.globalCompositeOperation = 'destination-out';
  g.globalAlpha = a;
  g.fillStyle = g.createPattern(dustTex(), 'repeat')!;
  g.fillRect(0, 0, W, H);
  g.restore();
}

export const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* One clock for every mark on the page. Each painter is called every frame and redraws only while it is
   drawing in or when the shiver frame (8 times a second) moves on. */
type Painter = (now: number, boil: number) => void;
const painters = new Set<Painter>();
let running = false;
function loop(now: number) {
  if (!painters.size) {
    running = false;
    return;
  }
  const boil = reducedMotion() ? 0 : Math.floor(now / 125) * 7;
  painters.forEach((p) => p(now, boil));
  requestAnimationFrame(loop);
}
export function addPainter(p: Painter) {
  painters.add(p);
  if (!running) {
    running = true;
    requestAnimationFrame(loop);
  }
  return () => {
    painters.delete(p);
  };
}
